import withStyles from "isomorphic-style-loader/withStyles";
import { NavigateFunction } from "react-router-dom";
import BaseInput from "../../../toolkit/baseInput";
import PrimaryButton from "../../../toolkit/primaryButton";
import { useLlmChatPrompt } from "../../../hooks/useLlmChatPrompt";
import { useChatSessionList } from "../chatSession/sessionList/useChatSessionList";
import IGlobalConfig from "../../../types/IGlobalConfig";
import s from "./styles.scss";

const SUGGESTED_PROMPTS = [
  "Which ViewSparks received the most donations?",
  "How many emails were sent this month?",
  "Compare donation amounts this month vs last month",
  "Which ViewSpark generated the most donations?",
  "Which subject lines or message copy got the most opens and clicks?",
  "Which send times got the most engagement?",
];

const RECENT_SESSION_LIMIT = 2;

interface Props {
  config: IGlobalConfig;
  serviceAccessToken: string;
  charityId?: string;
  navigate: NavigateFunction;
}

function LlmChatPrompt({
  config,
  serviceAccessToken,
  charityId,
  navigate,
}: Props) {
  const {
    message,
    loading,
    error,
    focused,
    handleMessageChange,
    handleSubmit,
    handleComposerKeyDown,
    handleFocus,
    handleComposerBlur,
    handleSuggestedPrompt,
    handleViewConversations,
  } = useLlmChatPrompt({ config, serviceAccessToken, charityId, navigate });

  const { sessions, handleSessionSelect } = useChatSessionList({
    config,
    serviceAccessToken,
    charityId,
  });
  const recentSessions = sessions.slice(0, RECENT_SESSION_LIMIT);

  return (
    <div className={`${s.card}${loading ? ` ${s.cardLoading}` : ""}`}>
      <div className={s.header}>
        <div className={s.heading}>
          <span className={s.iconWrap} aria-hidden="true">
            <i className="fa fa-comments" />
          </span>
          <div className={s.headingCopy}>
            <div className={s.titleRow}>
              <p className={s.title}>Ask about your metrics</p>
              <span className={s.badge}>New</span>
            </div>
            <p className={s.subtitle}>
              Summarize trends, compare charities, or drill into a date range.
            </p>
          </div>
        </div>
        <button
          type="button"
          className={s.conversationsLink}
          onClick={handleViewConversations}
          data-elm-id="llmChatViewConversations"
        >
          View conversations
        </button>
      </div>

      <div className={`${s.chips}${focused ? ` ${s.chipsDimmed}` : ""}`}>
        {SUGGESTED_PROMPTS.map(prompt => (
          <button
            key={prompt}
            type="button"
            className={s.chip}
            onClick={() => handleSuggestedPrompt(prompt)}
            disabled={loading}
            data-elm-id="llmChatSuggestedPrompt"
          >
            {prompt}
          </button>
        ))}
      </div>

      <form
        className={s.composer}
        onSubmit={handleSubmit}
        onKeyDown={handleComposerKeyDown}
      >
        <div
          className={s.composerField}
          onFocus={handleFocus}
          onBlur={handleComposerBlur}
        >
          <div className={s.inputWrapper}>
            <BaseInput
              type="textarea"
              name="llmChatPrompt"
              id="llmChatPromptInput"
              value={message}
              onChange={handleMessageChange}
              onFocus={handleFocus}
              placeholder="Ask a question about your metrics..."
              disabled={loading}
              dataQAId="llmChatPrompt"
              dataElmId="llmChatPrompt"
              errors={[]}
              options={[]}
              rows={1}
              customClassName={s.input}
            />
          </div>
          <PrimaryButton
            type="submit"
            disabled={!message.trim() || loading}
            loading={loading}
            loadingText="Thinking"
            className={s.submitButton}
          >
            Ask
          </PrimaryButton>
        </div>
        <div className={s.composerMeta}>
          {error ? (
            <span className={s.error}>{error}</span>
          ) : focused && !loading ? (
            <span className={s.hint}>Enter to ask</span>
          ) : null}
        </div>
      </form>

      {recentSessions.length > 0 && (
        <div className={s.recents}>
          <p className={s.recentsLabel}>Continue a conversation</p>
          {recentSessions.map(session => (
            <button
              key={session.sessionId}
              type="button"
              className={s.recentRow}
              onClick={() => handleSessionSelect(session.sessionId)}
              disabled={loading}
              data-elm-id={`llmChatRecent-${session.sessionId}`}
            >
              <span className={s.recentPreview}>{session.preview}</span>
              <i className={`fa fa-chevron-right ${s.recentChevron}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default withStyles(s)(LlmChatPrompt);
